import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { ReviewerSignerPicker } from "components/Submission/ReviewerSignerPicker";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { Country, SignatureType } from "generated-graphql/graphql";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { BULK_SET_SIGNER_MUTATION } from "queries/submissions";
import { useMemo, useState } from "react";
import { formatDateLikeForDisplay, toUtcMidnightIsoFromDateLike } from "util/dates";
import { STATUS_LABELS, isSubmissionLockedForBulkAction } from "utils/submissionStatusLabels";

import { BulkActionSubmission } from "./types";

type BulkSetSignerDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: BulkActionSubmission[];
  onSuccess: () => void;
  /** GraphQL queries to refetch after mutation */
  refetchQueries?: string[];
};

export function BulkSetSignerDialog({
  open,
  onClose,
  submissions,
  onSuccess,
  refetchQueries = [],
}: BulkSetSignerDialogProps) {
  const alerts = useAlerts();

  // Signer state
  const [signer, setSigner] = useState<SubmissionReviewerSigner | null>(null);
  const [dueDate, setDueDate] = useState<DateTime | null>(null);
  const [signatureType, setSignatureType] = useState<SignatureType>(SignatureType.Digital);
  // Document prep overrides
  const [signerNameOverride, setSignerNameOverride] = useState("");
  const [signerEmailOverride, setSignerEmailOverride] = useState("");

  const isEmailOverrideInvalid =
    signerEmailOverride.length > 0 &&
    !/^([^\s@.]+\.)*[^\s@.]+@[^\s@.]+(\.[^\s@.]+)+$/.test(signerEmailOverride);

  // Address fields for wet-ink signatures
  const [streetAddress1, setStreetAddress1] = useState("");
  const [streetAddress2, setStreetAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("");

  const [bulkSetSigner, { loading }] = useMutation(BULK_SET_SIGNER_MUTATION, {
    refetchQueries,
    awaitRefetchQueries: refetchQueries.length > 0,
  });

  // Partition virtual events from real submissions and filter out locked statuses
  const { virtualEvents, lockedSubmissions, eligibleSubmissions, eligibleSubmissionIds } =
    useMemo(() => {
      const virtualEvents: BulkActionSubmission[] = [];
      const materialized: BulkActionSubmission[] = [];

      for (const row of submissions) {
        if (row.isVirtualEvent) {
          virtualEvents.push(row);
        } else {
          materialized.push(row);
        }
      }

      // Separate locked from eligible
      const lockedSubmissions = materialized.filter((s) =>
        isSubmissionLockedForBulkAction(s.status)
      );
      const eligibleSubmissions = materialized.filter(
        (s) => !isSubmissionLockedForBulkAction(s.status)
      );
      const eligibleSubmissionIds = eligibleSubmissions
        .map((s) => s.submissionId)
        .filter((id): id is string => Boolean(id));

      return {
        virtualEvents,
        lockedSubmissions,
        eligibleSubmissions,
        eligibleSubmissionIds,
      };
    }, [submissions]);

  const tenantId = useMemo(() => {
    const tenantIds = new Set(
      submissions.map((s) => s.tenantId).filter((id): id is string => Boolean(id))
    );
    return tenantIds.size === 1 ? [...tenantIds][0] : undefined;
  }, [submissions]);

  const handleClose = () => {
    // Reset state
    setSigner(null);
    setDueDate(null);
    setSignatureType(SignatureType.Digital);
    setSignerNameOverride("");
    setSignerEmailOverride("");
    setStreetAddress1("");
    setStreetAddress2("");
    setCity("");
    setState("");
    setZip("");
    setCountry("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!signer?.id) {
      alerts.error("Please select a signer");
      return;
    }

    if (eligibleSubmissionIds.length === 0) {
      alerts.error(
        "No eligible submissions selected. Virtual events must be started and submissions must not be in review or complete."
      );
      return;
    }

    try {
      const result = await bulkSetSigner({
        variables: {
          input: {
            submissionIds: eligibleSubmissionIds,
            signer: {
              userId: signer.id,
              isStaff: signer.isStaff ?? false,
              isRequired: true,
              isSigner: true,
              dueDate: dueDate ? (toUtcMidnightIsoFromDateLike(dueDate) ?? null) : null,
              // Include address fields for wet-ink signatures
              signerNameOverride: signerNameOverride || null,
              signerEmailOverride: signerEmailOverride || null,
              ...(signatureType === SignatureType.WetInk && {
                streetAddress1: streetAddress1 || undefined,
                streetAddress2: streetAddress2 || undefined,
                city: city || undefined,
                state: state || undefined,
                zip: zip || undefined,
                country: country || undefined,
              }),
            },
            signatureType,
          },
        },
      });

      if (result.data?.bulkSetSigner.succeeded) {
        const skippedCount = virtualEvents.length + lockedSubmissions.length;
        const skippedRejected = result.data.bulkSetSigner.skippedRejected ?? 0;
        alerts.success(
          `Successfully set signer for ${result.data.bulkSetSigner.updated} submission(s)` +
            (skippedCount > 0 ? `. ${skippedCount} submission(s) skipped.` : "") +
            (skippedRejected > 0
              ? ` ${skippedRejected} rejected submission(s) skipped — resolve the rejection first.`
              : "")
        );
        handleClose();
        onSuccess();
      } else {
        alerts.error("Failed to set signer");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      alerts.error(`Failed to set signer: ${message}`);
    }
  };

  const tableRows = useMemo(() => {
    // Eligible submissions
    const eligibleRows = eligibleSubmissions.map((s) => ({
      id: s.id,
      isVirtual: false,
      isLocked: false,
      dueDate: s.dueDate,
      facility: s.facilityName,
      submissionType: s.submissionType,
      title: s.title,
      status: s.status,
    }));

    // Locked submissions (will be skipped)
    const lockedRows = lockedSubmissions.map((s) => ({
      id: s.id,
      isVirtual: false,
      isLocked: true,
      dueDate: s.dueDate,
      facility: s.facilityName,
      submissionType: s.submissionType,
      title: s.title,
      status: s.status,
    }));

    // Virtual events (will be skipped)
    const virtualRows = virtualEvents.map((s) => ({
      id: s.id,
      isVirtual: true,
      isLocked: false,
      dueDate: s.dueDate,
      facility: s.facilityName,
      submissionType: s.submissionType,
      title: s.title,
      status: null as string | null,
    }));

    return [...eligibleRows, ...lockedRows, ...virtualRows];
  }, [eligibleSubmissions, lockedSubmissions, virtualEvents]);

  const columns: CustomGridColDef<(typeof tableRows)[number]>[] = [
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 100,
      valueGetter: (value, row) => (row.dueDate ? formatDateLikeForDisplay(row.dueDate) : ""),
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "submissionType",
      headerName: "Submission Type",
      width: 130,
      valueGetter: (value, row) => prettyPrintEnumValue(row.submissionType),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      valueGetter: (value, row) => {
        if (row.isVirtual) return "Virtual (skipped)";
        if (row.isLocked && row.status) {
          return `${STATUS_LABELS[row.status] ?? prettyPrintEnumValue(row.status)} (skipped)`;
        }
        if (row.status) {
          return STATUS_LABELS[row.status] ?? prettyPrintEnumValue(row.status);
        }
        return "";
      },
    },
  ];

  const isWetInk = signatureType === SignatureType.WetInk;

  const canSubmit =
    Boolean(signer?.id) &&
    (isWetInk || !isEmailOverrideInvalid) &&
    eligibleSubmissionIds.length > 0 &&
    (isWetInk ? streetAddress1.trim() : true);
  const skippedCount = virtualEvents.length + lockedSubmissions.length;

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        title={`Add signer to ${submissions.length} submissions`}
        description={
          <>
            Set a signer for the selected submissions. This will replace any existing signer on each
            submission.
          </>
        }
        primaryActionButtonProps={{
          saveText: loading ? "SAVING..." : "SAVE",
          onClick: handleSubmit,
          disabled: loading || !canSubmit,
        }}
        cancelButtonProps={{
          text: "CANCEL",
          onClick: handleClose,
        }}
      >
        <Stack spacing={3}>
          {/* Signer Selection */}
          <Stack direction="row" spacing={2}>
            <ReviewerSignerPicker
              onChange={(newSigner) => setSigner(newSigner)}
              value={signer}
              tenantId={tenantId}
            />
            <Box>
              <DatePicker
                label="Due date"
                value={dueDate}
                onChange={(newValue) => setDueDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </Stack>

          {/* Document prep overrides */}
          <TextField
            label="Full Name"
            value={signerNameOverride}
            onChange={(e) => setSignerNameOverride(e.target.value)}
            fullWidth
            size="small"
            helperText="Leave blank to use signer's account name"
          />
          {!isWetInk && (
            <TextField
              label="BoldSign Email Override"
              value={signerEmailOverride}
              onChange={(e) => setSignerEmailOverride(e.target.value)}
              fullWidth
              size="small"
              error={isEmailOverrideInvalid}
              helperText={
                isEmailOverrideInvalid
                  ? "Please enter a valid email address"
                  : "Leave blank to use signer's account email"
              }
            />
          )}

          {/* Signature Type */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Signature Type
            </Typography>
            <RadioGroup
              row
              value={signatureType}
              onChange={(e) => setSignatureType(e.target.value as SignatureType)}
            >
              <FormControlLabel
                value={SignatureType.Digital}
                control={<Radio size="small" />}
                label={<Typography variant="body2">Digital signature</Typography>}
              />
              <FormControlLabel
                value={SignatureType.WetInk}
                control={<Radio size="small" />}
                label={<Typography variant="body2">Wet-ink signature</Typography>}
              />
            </RadioGroup>
          </Box>

          {/* Address fields for wet-ink signatures */}
          {signatureType === SignatureType.WetInk && (
            <Stack spacing={3}>
              <TextField
                label="Street Address 1 *"
                value={streetAddress1}
                onChange={(e) => setStreetAddress1(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Street Address 2"
                value={streetAddress2}
                onChange={(e) => setStreetAddress2(e.target.value)}
                fullWidth
                size="small"
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="State/Territory"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  sx={{ width: "30%" }}
                  size="small"
                />
                <TextField
                  label="ZIP"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  sx={{ width: "30%" }}
                  size="small"
                />
              </Stack>
              <FormControl fullWidth size="small">
                <InputLabel id="bulk-signer-country-label">Country</InputLabel>
                <Select
                  labelId="bulk-signer-country-label"
                  label="Country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <MenuItem value={Country.Usa}>United States</MenuItem>
                  <MenuItem value={Country.Canada}>Canada</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {/* Preview Table */}
          <Typography variant="subtitle2">Submissions ({submissions.length} selected)</Typography>
          <DataGrid
            rows={tableRows}
            columns={columns}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={[5, 10]}
            hideFooterPagination={false}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            sx={{ maxHeight: 300 }}
          />

          {skippedCount > 0 && (
            <Alert severity="info">
              {virtualEvents.length > 0 &&
                `${virtualEvents.length} ${pluralize(
                  "submission",
                  virtualEvents.length
                )} must be started before a signer can be set. `}
              {lockedSubmissions.length > 0 &&
                `${lockedSubmissions.length} ${pluralize("submission", lockedSubmissions.length)} ${
                  lockedSubmissions.length === 1 ? "is" : "are"
                } in a locked status and will be skipped.`}
            </Alert>
          )}
        </Stack>
      </Dialog>
    </LocalizationProvider>
  );
}
